import { keepPreviousData } from '@tanstack/react-query';
import { PaginationState } from '@tanstack/react-table';
import { useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';

import { api } from 'api';

const STALE_TIME = 5 * 60 * 1000; // 5 minutes
const CACHE_TIME = 10 * 60 * 1000; // 10 minutes

type InvoiceFilter = {
  field: string;
  operator: string;
  value: string | boolean;
};

type UseInvoicesOptions = {
  enabled?: boolean;
  refetchInterval?: number | false;
  onSuccess?: (data: unknown) => void;
  onError?: (error: Error) => void;
};

/**
 * Custom hook for fetching invoices with support for URL-based filters and pagination.
 */
const useInvoices = (
  pagination?: PaginationState,
  options?: UseInvoicesOptions
) => {
  const [searchParams] = useSearchParams();

  const filterParams = useMemo(
    () => ({
      customerId: searchParams.get('customer_id'),
      status: searchParams.get('status'),
      paid: searchParams.get('paid'),
      finalized: searchParams.get('finalized'),
      dateFrom: searchParams.get('date_from'),
      dateTo: searchParams.get('date_to'),
    }),
    [searchParams]
  );

  const filters = useMemo(() => {
    const result: InvoiceFilter[] = [];

    if (filterParams.customerId) {
      result.push({
        field: 'customer_id',
        operator: 'eq',
        value: filterParams.customerId,
      });
    }

    if (filterParams.status) {
      result.push({ field: filterParams.status, operator: 'eq', value: true });
    }

    if (filterParams.paid !== null) {
      result.push({
        field: 'paid',
        operator: 'eq',
        value: filterParams.paid === 'true',
      });
    }

    if (filterParams.finalized !== null) {
      result.push({
        field: 'finalized',
        operator: 'eq',
        value: filterParams.finalized === 'true',
      });
    }

    if (filterParams.dateFrom) {
      result.push({
        field: 'date',
        operator: 'gt',
        value: filterParams.dateFrom,
      });
    }

    if (filterParams.dateTo) {
      result.push({
        field: 'date',
        operator: 'lt',
        value: filterParams.dateTo,
      });
    }

    return result.length ? JSON.stringify(result) : undefined;
  }, [filterParams]);

  const currentPage = pagination?.pageIndex ? pagination.pageIndex + 1 : 1;
  const pageSize = pagination?.pageSize ?? 10;

  return api.useQuery(
    'get',
    '/invoices',
    {
      params: {
        query: {
          page: currentPage,
          per_page: pageSize,
          filter: filters,
        },
      },
    },
    {
      placeholderData: keepPreviousData,
      staleTime: STALE_TIME,
      gcTime: CACHE_TIME,
      ...options,
    }
  );
};

export default useInvoices;
